/**
 * My Goals Service
 *
 * Service layer for interacting with the My Goals state.
 * Provides methods for loading and managing goals for students in the current user's caseload from Firestore.
 *
 * Goals documents are stored at: partners/{partnerId}/goals/{goalId}
 *
 * Real-time sync is maintained to keep the local state up to date with Firestore changes.
 */

import { Injectable, inject, OnDestroy } from '@angular/core';
import { Store, Select } from '@ngxs/store';
import { Observable, Subscription } from 'rxjs';
import { distinctUntilChanged } from 'rxjs/operators';
import { MyGoalsState, MyGoalsStateModel } from './my-goals.state';
import {
  Initialize,
  SetGoals,
  SetInitialized,
  AddGoal,
  UpdateGoal,
  DeleteGoal,
  ClearMyGoals,
} from './my-goals.actions';
import { FirestoreService } from 'src/app/services/firestore/firestore';
import { Goal, ActivityType, ActivityCategory, Objective } from 'src/app/models/schema';
import { ActiveTeamState } from '../../account/active-team';
import { AuthState } from '../../../store/auth';
import { AccountDetailsState } from 'src/app/rxjs-store/account/account-details';
import { ProfileState } from 'src/app/rxjs-store/account/profile';
import { MyObjectivesState } from '../my-objectives';
import { TeamActivityService } from 'src/app/rxjs-store/account/team-activity';
import { UtilsService } from 'src/app/services/utils.service';
import { Unsubscribe } from '@angular/fire/firestore';

/**
 * My Goals Service
 *
 * Handles all my-goals-related operations including loading from
 * and saving to Firestore with real-time sync support.
 */
@Injectable({
  providedIn: 'root',
})
export class MyGoalsService implements OnDestroy {
  private store: Store = inject(Store);
  private firestoreService: FirestoreService = inject(FirestoreService);
  private utilsService: UtilsService = inject(UtilsService);
  private teamActivityService: TeamActivityService =
    inject(TeamActivityService);

  /**
   * Active goals listener unsubscribe function
   */
  private goalsUnsubscribe?: Unsubscribe;

  /**
   * Profile caseload subscription
   */
  private caseloadSubscription?: Subscription;

  /**
   * Objectives subscription (for syncing goal.objectives array)
   */
  private objectivesSubscription?: Subscription;

  /**
   * Cache of all goals loaded from Firestore (before filtering)
   */
  private allGoalsCache: Goal[] = [];

  // ========================================
  // Observable Selectors
  // ========================================

  /**
   * Observable of the entire my goals state
   */
  @Select(MyGoalsState.getState)
  myGoalsState$!: Observable<MyGoalsStateModel>;

  /**
   * Observable of all goals
   */
  @Select(MyGoalsState.goals)
  goals$!: Observable<Goal[]>;

  /**
   * Observable of goal IDs
   */
  @Select(MyGoalsState.goalIds)
  goalIds$!: Observable<string[]>;

  /**
   * Observable of goal count
   */
  @Select(MyGoalsState.goalCount)
  goalCount$!: Observable<number>;

  /**
   * Observable of initialized state
   */
  @Select(MyGoalsState.initialized)
  initialized$!: Observable<boolean>;

  /**
   * Observable of whether there are goals
   */
  @Select(MyGoalsState.hasGoals)
  hasGoals$!: Observable<boolean>;

  /**
   * Observable of archived goals
   */
  @Select(MyGoalsState.archivedGoals)
  archivedGoals$!: Observable<Goal[]>;

  /**
   * Observable of active goals
   */
  @Select(MyGoalsState.activeGoals)
  activeGoals$!: Observable<Goal[]>;

  /**
   * Observable of goals sorted by date
   */
  @Select(MyGoalsState.goalsSortedByDate)
  goalsSortedByDate$!: Observable<Goal[]>;

  /**
   * Observable of goals sorted by target date
   */
  @Select(MyGoalsState.goalsSortedByTargetDate)
  goalsSortedByTargetDate$!: Observable<Goal[]>;

  // ========================================
  // Synchronous Getters
  // ========================================

  /**
   * Get all goals (synchronous)
   */
  get goals(): Goal[] {
    return this.store.selectSnapshot(MyGoalsState.goals);
  }

  /**
   * Get goal IDs (synchronous)
   */
  get goalIds(): string[] {
    return this.store.selectSnapshot(MyGoalsState.goalIds);
  }

  /**
   * Get goal count (synchronous)
   */
  get goalCount(): number {
    return this.store.selectSnapshot(MyGoalsState.goalCount);
  }

  /**
   * Get initialized state (synchronous)
   */
  get isInitialized(): boolean {
    return this.store.selectSnapshot(MyGoalsState.initialized);
  }

  /**
   * Check if there are goals (synchronous)
   */
  get hasGoals(): boolean {
    return this.store.selectSnapshot(MyGoalsState.hasGoals);
  }

  /**
   * Get archived goals (synchronous)
   */
  get archivedGoals(): Goal[] {
    return this.store.selectSnapshot(MyGoalsState.archivedGoals);
  }

  /**
   * Get active goals (synchronous)
   */
  get activeGoals(): Goal[] {
    return this.store.selectSnapshot(MyGoalsState.activeGoals);
  }

  /**
   * Get a specific goal by ID (synchronous)
   */
  getGoal(id: string): Goal | null {
    return this.store.selectSnapshot(MyGoalsState.goal(id));
  }

  /**
   * Get goals by student ID (synchronous)
   */
  getGoalsByStudentId(studentId: string): Goal[] {
    return this.store.selectSnapshot(MyGoalsState.goalsByStudentId(studentId));
  }

  // ========================================
  // Helper Methods
  // ========================================

  /**
   * Convert undefined values to null for Firestore compatibility
   * Firestore doesn't support undefined values, so we convert them to null
   */
  private convertUndefinedToNull<T extends Record<string, any>>(obj: T): T {
    const result: any = {};
    for (const key in obj) {
      if (obj.hasOwnProperty(key)) {
        const value = obj[key];
        if (value === undefined) {
          result[key] = null;
        } else if (
          value !== null &&
          typeof value === 'object' &&
          !Array.isArray(value)
        ) {
          // Recursively handle nested objects
          result[key] = this.convertUndefinedToNull(value);
        } else {
          result[key] = value;
        }
      }
    }
    return result as T;
  }

  // ========================================
  // Service Methods
  // ========================================

  /**
   * Initialize my goals service
   *
   * Loads goals for students in the active user's caseload and sets up real-time sync.
   * If the user has no caseload or an empty caseload, sets initialized to true
   * with an empty goals array.
   * 
   * Subscribes to both:
   * 1. Goals collection changes (real-time updates to goal data)
   * 2. Profile caseload changes (real-time updates to which goals to show)
   */
  async initialize(): Promise<void> {
    console.log('[MyGoalsService] Initializing...');

    // Dispatch initialize action
    this.store.dispatch(new Initialize());

    // Clean up existing listeners if any
    if (this.goalsUnsubscribe) {
      this.goalsUnsubscribe();
      this.goalsUnsubscribe = undefined;
    }
    if (this.caseloadSubscription) {
      this.caseloadSubscription.unsubscribe();
      this.caseloadSubscription = undefined;
    }

    // Get active team
    const activeTeamId = this.store.selectSnapshot(ActiveTeamState.activeTeamId);
    if (!activeTeamId) {
      console.warn('[MyGoalsService] No active team, setting initialized with empty goals');
      this.store.dispatch(new SetGoals([]));
      this.store.dispatch(new SetInitialized(true));
      return;
    }

    // Subscribe to profile caseload changes for real-time filtering
    this.caseloadSubscription = this.store
      .select(ProfileState.profile)
      .pipe(distinctUntilChanged((prev, curr) => {
        // Only re-filter if caseload actually changed
        const prevCaseload = prev?.caseload || [];
        const currCaseload = curr?.caseload || [];
        return JSON.stringify(prevCaseload.sort()) === JSON.stringify(currCaseload.sort());
      }))
      .subscribe((profile) => {
        const caseload = profile?.caseload || [];
        console.log(`[MyGoalsService] Caseload updated: ${caseload.length} students`);
        
        // Re-filter cached goals with new caseload
        this.filterAndUpdateGoals(caseload);
      });

    // Subscribe to MyObjectives state changes to sync goal.objectives array
    this.objectivesSubscription = this.store
      .select(MyObjectivesState.objectives)
      .pipe(distinctUntilChanged())
      .subscribe((allObjectives) => {
        console.log(`[MyGoalsService] Objectives updated: ${allObjectives.length}, syncing with goals`);
        this.syncObjectivesWithGoals(allObjectives);
      });

    // Get initial caseload
    const profile = this.store.selectSnapshot(ProfileState.profile);
    const initialCaseload = profile?.caseload || [];

    // If no caseload or empty caseload, set initialized with empty goals
    if (!initialCaseload || initialCaseload.length === 0) {
      console.log('[MyGoalsService] No caseload found, setting initialized with empty goals');
      this.store.dispatch(new SetGoals([]));
      this.store.dispatch(new SetInitialized(true));
      // Still set up goals listener so we can react if caseload gets populated later
    }

    return new Promise((resolve, reject) => {
      let isInitialLoad = true;

      try {
        // Collection path
        const collectionPath = `partners/${activeTeamId}/goals`;

        console.log(`[MyGoalsService] Setting up real-time listener for goals`);

        // Set up real-time listener for all goals (no where clause as per requirements)
        this.goalsUnsubscribe =
          this.firestoreService.onCollectionChange<Goal>(
            collectionPath,
            (allGoals) => {
              // Cache all goals
              this.allGoalsCache = allGoals;
              
              // Get current caseload from state
              const currentProfile = this.store.selectSnapshot(ProfileState.profile);
              const currentCaseload = currentProfile?.caseload || [];

              // Filter goals by current caseload
              this.filterAndUpdateGoals(currentCaseload);

              console.log(
                `[MyGoalsService] Loaded ${allGoals.length} total goals, filtered to caseload`
              );

              // Resolve promise on initial load
              if (isInitialLoad) {
                isInitialLoad = false;
                resolve();
              }
            },
            {} // No query options - load all goals
          );
      } catch (error: any) {
        const errorMessage = error.message || 'Failed to load my goals';
        console.error(
          '[MyGoalsService] Error loading my goals:',
          errorMessage
        );
        // Still set initialized even on error
        this.store.dispatch(new SetGoals([]));
        this.store.dispatch(new SetInitialized(true));
        reject(error);
      }
    });
  }

  /**
   * Filter cached goals by caseload and update state
   * 
   * Goals are filtered by checking if goal.studentId is in the caseload array
   * 
   * @param caseload - Array of student IDs to include
   */
  private filterAndUpdateGoals(caseload: string[]): void {
    // Filter cached goals by caseload student IDs (client-side filtering)
    const filteredGoals = this.allGoalsCache.filter((goal) => {
      return goal.studentId && caseload.includes(goal.studentId);
    });

    // Dispatch success action
    this.store.dispatch(new SetGoals(filteredGoals));
    this.store.dispatch(new SetInitialized(true));

    console.log(
      `[MyGoalsService] Filtered to ${filteredGoals.length} goals for ${caseload.length} students in caseload`
    );
  }

  /**
   * Sync objectives with goals
   * 
   * Updates each goal's objectives array and objectiveIds array to match
   * the current state of objectives in MyObjectivesState.
   * 
   * This ensures goals always display the latest objective data without
   * needing to update the goal document in Firestore.
   * 
   * @param allObjectives - All objectives from MyObjectivesState
   */
  private syncObjectivesWithGoals(allObjectives: Objective[]): void {
    const currentGoals = this.store.selectSnapshot(MyGoalsState.goals);
    
    if (currentGoals.length === 0) {
      return; // No goals to sync
    }

    // For each goal, find and sync its objectives
    currentGoals.forEach(goal => {
      // Find all objectives that belong to this goal
      const goalObjectives = allObjectives.filter(obj => obj.goalId === goal.id);
      const goalObjectiveIds = goalObjectives.map(obj => obj.id);
      
      // Check if objectives actually changed to avoid unnecessary updates
      const currentObjectiveIds = goal.objectiveIds || [];
      const objectivesChanged = 
        JSON.stringify(currentObjectiveIds.sort()) !== JSON.stringify(goalObjectiveIds.sort());
      
      if (objectivesChanged) {
        console.log(`[MyGoalsService] Syncing objectives for goal ${goal.id}: ${goalObjectiveIds.length} objectives`);
        
        // Update the goal with latest objectives data
        this.store.dispatch(new UpdateGoal(goal.id, {
          objectives: goalObjectives,
          objectiveIds: goalObjectiveIds,
          noOfObjectives: goalObjectives.length,
        }));
      }
    });
  }

  /**
   * Create a new goal in Firestore
   *
   * @param goalData - Partial goal data to create
   * @returns Promise that resolves with the created goal ID
   */
  async createGoal(goalData: Partial<Goal>): Promise<string> {
    // Validate required fields
    if (
      !goalData.studentId ||
      !goalData.majorSkillAreaId ||
      !goalData.subsetSkillAreaId ||
      !goalData.targetDate
    ) {
      throw new Error(
        'Missing required fields: studentId, majorSkillAreaId, subsetSkillAreaId, or targetDate'
      );
    }

    const activeTeamId = this.store.selectSnapshot(ActiveTeamState.activeTeamId);
    if (!activeTeamId) {
      throw new Error('No active team selected');
    }

    const currentUserEmail =
      this.store.selectSnapshot(AuthState.userEmail) || 'unknown';
    const currentUserId =
      this.store.selectSnapshot(AuthState.userId) || 'unknown';
    const currentAccount = this.store.selectSnapshot(
      AccountDetailsState.account
    );
    const currentUserFirstName = currentAccount?.firstName || 'unknown';
    const currentUserLastName = currentAccount?.lastName || 'unknown';
    const now = Date.now();

    // Generate unique ID for goal
    const generatedId = this.utilsService.generateId();

    // Create complete goal object
    const goalToCreate: Partial<Goal> = {
      id: generatedId,
      studentId: goalData.studentId,
      majorSkillAreaId: goalData.majorSkillAreaId,
      subsetSkillAreaId: goalData.subsetSkillAreaId,
      skillIds: goalData.skillIds || [],
      observationFrequency: goalData.observationFrequency || 0,
      objectives: goalData.objectives || [],
      targetDate: goalData.targetDate,
      noOfObjectives: goalData.objectives?.length || 0,
      archived: goalData.archived || false,
      createdAt: now,
      createdBy: currentUserEmail,
      updatedAt: now,
      updatedBy: currentUserEmail,
    };

    // Convert undefined to null for Firestore
    const cleanedGoal = this.convertUndefinedToNull(goalToCreate);

    // Optimistically add to local state
    this.store.dispatch(new AddGoal(cleanedGoal as Goal));

    try {
      // Save to Firestore
      const documentPath = `partners/${activeTeamId}/goals/${generatedId}`;
      await this.firestoreService.setDocumentAsync(
        documentPath,
        cleanedGoal,
        true
      );

      // Log activity
      await this.teamActivityService.addActivity(activeTeamId, {
        uid: currentUserId,
        email: currentUserEmail,
        firstName: currentUserFirstName,
        lastName: currentUserLastName,
        category: ActivityCategory.TEAM,
        activityType: ActivityType.CREATED_STUDENT, // You may want a CREATED_GOAL type
        details: {
          personalNarrative: `You created a goal for student ${goalData.studentId}`,
          teamNarrative: `${currentUserEmail} created a goal for student ${goalData.studentId}`,
          goalId: generatedId,
          studentId: goalData.studentId,
        },
      });

      console.log(
        `[MyGoalsService] Created goal ${generatedId} for student ${goalData.studentId}`
      );

      return generatedId;
    } catch (error: any) {
      // Revert optimistic update on error
      this.store.dispatch(new DeleteGoal(generatedId));
      console.error('[MyGoalsService] Failed to create goal:', error);
      throw error;
    }
  }

  /**
   * Update an existing goal in Firestore
   *
   * @param goalId - Goal ID to update
   * @param updates - Partial goal data to update
   */
  async updateGoalInFirestore(
    goalId: string,
    updates: Partial<Goal>
  ): Promise<void> {
    const activeTeamId = this.store.selectSnapshot(ActiveTeamState.activeTeamId);
    if (!activeTeamId) {
      throw new Error('No active team selected');
    }

    const currentUserEmail =
      this.store.selectSnapshot(AuthState.userEmail) || 'unknown';
    const now = Date.now();

    // Add metadata
    const updatesToSave = {
      ...updates,
      updatedAt: now,
      updatedBy: currentUserEmail,
    };

    // Convert undefined to null for Firestore
    const cleanedUpdates = this.convertUndefinedToNull(updatesToSave);

    // Optimistically update local state
    this.store.dispatch(new UpdateGoal(goalId, cleanedUpdates));

    try {
      // Update in Firestore
      const documentPath = `partners/${activeTeamId}/goals/${goalId}`;
      await this.firestoreService.updateDocumentAsync(
        documentPath,
        cleanedUpdates
      );

      console.log(`[MyGoalsService] Updated goal ${goalId}`);
    } catch (error: any) {
      console.error('[MyGoalsService] Failed to update goal:', error);
      // Note: Real-time listener will revert the optimistic update
      throw error;
    }
  }

  /**
   * Delete a goal from Firestore
   *
   * @param goalId - Goal ID to delete
   */
  async deleteGoalFromFirestore(goalId: string): Promise<void> {
    const activeTeamId = this.store.selectSnapshot(ActiveTeamState.activeTeamId);
    if (!activeTeamId) {
      throw new Error('No active team selected');
    }

    const currentUserId =
      this.store.selectSnapshot(AuthState.userId) || 'unknown';
    const currentUserEmail =
      this.store.selectSnapshot(AuthState.userEmail) || 'unknown';
    const currentAccount = this.store.selectSnapshot(
      AccountDetailsState.account
    );
    const currentUserFirstName = currentAccount?.firstName || 'unknown';
    const currentUserLastName = currentAccount?.lastName || 'unknown';

    // Get goal data before deletion for activity log
    const goal = this.getGoal(goalId);

    // Optimistically delete from local state
    this.store.dispatch(new DeleteGoal(goalId));

    try {
      // Delete from Firestore
      const documentPath = `partners/${activeTeamId}/goals/${goalId}`;
      await this.firestoreService.deleteDocumentAsync(documentPath);

      // Log activity if we have goal data
      if (goal) {
        await this.teamActivityService.addActivity(activeTeamId, {
          uid: currentUserId,
          email: currentUserEmail,
          firstName: currentUserFirstName,
          lastName: currentUserLastName,
          category: ActivityCategory.TEAM,
          activityType: ActivityType.CREATED_STUDENT, // You may want a DELETED_GOAL type
          details: {
            personalNarrative: `You deleted a goal for student ${goal.studentId}`,
            teamNarrative: `${currentUserEmail} deleted a goal for student ${goal.studentId}`,
            goalId: goalId,
            studentId: goal.studentId,
          },
        });
      }

      console.log(`[MyGoalsService] Deleted goal ${goalId}`);
    } catch (error: any) {
      // Revert optimistic update on error (re-add goal)
      if (goal) {
        this.store.dispatch(new AddGoal(goal));
      }
      console.error('[MyGoalsService] Failed to delete goal:', error);
      throw error;
    }
  }

  /**
   * Clear all my goals
   */
  clearMyGoals(): void {
    // Clean up listeners
    if (this.goalsUnsubscribe) {
      this.goalsUnsubscribe();
      this.goalsUnsubscribe = undefined;
    }
    if (this.caseloadSubscription) {
      this.caseloadSubscription.unsubscribe();
      this.caseloadSubscription = undefined;
    }
    if (this.objectivesSubscription) {
      this.objectivesSubscription.unsubscribe();
      this.objectivesSubscription = undefined;
    }

    // Clear cache
    this.allGoalsCache = [];

    // Dispatch clear action
    this.store.dispatch(new ClearMyGoals());
  }

  /**
   * Cleanup on service destruction
   */
  ngOnDestroy(): void {
    if (this.goalsUnsubscribe) {
      this.goalsUnsubscribe();
      this.goalsUnsubscribe = undefined;
    }
    if (this.caseloadSubscription) {
      this.caseloadSubscription.unsubscribe();
      this.caseloadSubscription = undefined;
    }
    if (this.objectivesSubscription) {
      this.objectivesSubscription.unsubscribe();
      this.objectivesSubscription = undefined;
    }
    this.allGoalsCache = [];
  }
}
